/**
 * Dashboard composition.
 *
 * The monolith renders the dashboard as six full-width bands stacked down the
 * page: greeting, metric grid, quick actions, coaching panel, recent workouts.
 * Every band is the same width and the same weight, so there is nothing to look
 * at first — the universal admin-shell shape, and no amount of colour or type
 * work fixes it, because it is a composition problem.
 *
 * This module rearranges the *rendered* nodes rather than editing the
 * 8,000-line core: nodes are moved, never recreated, so inline `onclick`
 * handlers and any listeners the core attached survive the move.
 *
 * The shape it builds:
 *
 *   ┌ toolbar ─────────────────────────────────────────────┐
 *   ├ hero ────────────────────────┬───────────────────────┤
 *   │ the headline metric, large   │ the supporting metrics│
 *   ├ body ────────────────────────┼───────────────────────┤
 *   │ recent workouts              │ rail: actions, coach  │
 *   └──────────────────────────────┴───────────────────────┘
 *
 * Every step is guarded: if the core's markup changes shape, the pieces it
 * cannot find are simply left where they are and the page still renders.
 */

import { onRendered } from '../runtime/legacy.js';

/** The metric that earns the hero slot, most preferred first. */
const HERO_PRIORITY = [/volume/i, /this week/i, /total workouts/i];

function pickHero(cards) {
  for (const pattern of HERO_PRIORITY) {
    const match = cards.find((c) =>
      pattern.test(c.querySelector('.stat-label')?.textContent || ''),
    );
    if (match) return match;
  }
  return cards[0];
}

/**
 * Reads the per-session volumes out of the rows the core has already rendered.
 *
 * Volume is not a stored field — the core derives it from every set in every
 * exercise. Recomputing it here would mean maintaining a second copy of that
 * arithmetic whose output sits inches from the first, so this parses the
 * numbers already on screen instead. Worst case it finds nothing and the
 * sparkline is skipped.
 */
function readSessionVolumes(recentCard) {
  const values = [];
  recentCard.querySelectorAll('.workout-row-stat').forEach((stat) => {
    if (!/volume/i.test(stat.querySelector('.workout-row-stat-label')?.textContent || '')) return;
    const raw = (stat.querySelector('.workout-row-stat-value')?.textContent || '').trim();
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    values.push(/k/i.test(raw) ? n * 1000 : n);
  });
  // Rows run newest-first; a trend reads left to right, oldest to newest.
  return values.reverse();
}

/** An inline SVG trend line: area, stroke, and one emphasised endpoint. */
function buildSparkline(values) {
  const W = 260;
  const H = 46;
  const PAD = 3;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const x = (i) => (i / (values.length - 1)) * (W - PAD * 2) + PAD;
  const y = (v) => H - PAD - ((v - min) / span) * (H - PAD * 2);
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(values.length - 1).toFixed(1)} ${H} L${x(0).toFixed(1)} ${H} Z`;

  const wrap = document.createElement('div');
  wrap.className = 'dash-spark';
  wrap.innerHTML = `
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="dash-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--accent-base)" stop-opacity="0.28"/>
          <stop offset="100%" stop-color="var(--accent-base)" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${area}" fill="url(#dash-spark-fill)"/>
      <path d="${line}" fill="none" stroke="var(--accent-base)" stroke-width="1.5"
            stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"/>
      <circle cx="${x(values.length - 1).toFixed(1)}" cy="${y(values[values.length - 1]).toFixed(1)}"
              r="2.5" fill="var(--accent-base)"/>
    </svg>
    <span class="dash-spark-caption">volume · last ${values.length} sessions</span>
  `;
  return wrap;
}

function restructure(root) {
  // Idempotent: the core re-renders the view on every route change, and
  // onRendered fires again for the fresh nodes.
  if (!root || root.querySelector('.dash-hero')) return;

  const stats = root.querySelector('.stats-grid');
  const recentCard = root.querySelector('#recent-list')?.closest('.card');
  if (!stats || !recentCard) return;

  const statCards = [...stats.children].filter((c) => c.classList.contains('stat-card'));
  if (statCards.length < 2) return;

  const hero = pickHero(statCards);
  const rest = statCards.filter((c) => c !== hero);

  // ── Hero: one metric at display scale, the rest as a supporting block ────
  const heroSection = document.createElement('section');
  heroSection.className = 'dash-hero';

  const lead = document.createElement('div');
  lead.className = 'dash-hero-lead';
  hero.classList.add('stat-card-hero');
  lead.appendChild(hero);

  const supporting = document.createElement('div');
  supporting.className = 'dash-hero-metrics';
  rest.forEach((c) => supporting.appendChild(c));

  // A trend beneath the headline number: it is what turns a large figure into
  // a reading, and it earns the space the hero takes.
  const volumes = readSessionVolumes(recentCard);
  if (volumes.length >= 3) lead.appendChild(buildSparkline(volumes));

  heroSection.append(lead, supporting);
  stats.replaceWith(heroSection);

  // ── Body: a main column and a rail, instead of two more full-width bands ─
  const body = document.createElement('div');
  body.className = 'dash-body';
  const main = document.createElement('div');
  main.className = 'dash-main';
  const rail = document.createElement('aside');
  rail.className = 'dash-rail';

  recentCard.replaceWith(body);
  main.appendChild(recentCard);

  // Anything else the core rendered below the hero belongs in the rail: the
  // quick actions and the coaching panel today, whatever is added tomorrow.
  const actions = root.querySelector('.quick-actions');
  if (actions) rail.appendChild(actions);
  root.querySelectorAll(':scope > .card').forEach((card) => {
    if (card !== recentCard) rail.appendChild(card);
  });

  body.append(main, rail);
  root.classList.add('is-composed');
}

export function installDashboardLayout() {
  // `#recent-list` exists only on the dashboard, which makes it a precise
  // signal that this particular view has just rendered.
  onRendered('#recent-list', (el) => {
    restructure(el.closest('#view-container'));
  });
}
